import { AfterViewInit, Component, OnDestroy, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { Title } from '@angular/platform-browser';
import { ConfigProvider } from "../../../config-provider.service";
import { OrganizationsNavigationProvider } from '../../../organizations/providers/navigation-provider';
import { CurrentUser } from '../../../common/providers/current-user';
import { CurrentUserRolesProvider } from '../../../organizations/providers/current-user-roles-provider';
import { TeamRow, TeamsProvider } from '../../providers/teams-provider';
import { CoachesProvider, CoachRow } from '../../../coaches/providers/coaches-provider';
import { AthleteRow, AthletesProvider } from '../../../athletes/providers/athletes-provider';
import { SimpleMatDataSource } from '../../../common/providers/simple-mat-data-source';
import { TeamsFormComponent } from '../../components/form/form';
import { BreadcrumbProvider } from '../../../breadcrumb/providers/breadcrumb-provider';
import { forkJoin, Observable, of, Subscription, switchMap, throwError } from 'rxjs';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort, MatSortModule } from '@angular/material/sort';
import { MatDialog } from '@angular/material/dialog';
import { HeaderComponent } from "../../../common/components/header-elitment/header-elitment";
import { Error401Component } from "../../../common/components/error-401/error-401";
import { MainActionComponent } from "../../../common/components/main-action/main-action";
import { ModuleContentComponent } from "../../../common/components/module-content/module-content";
import { Error404Component } from "../../../common/components/error-404/error-404";
import { PageContentComponent } from "../../../common/components/page-content/page-content";
import { MatIconModule } from "@angular/material/icon";
import { MatMenu, MatMenuTrigger } from "@angular/material/menu";
import { MatTableModule } from "@angular/material/table";
import { NgIf } from "@angular/common";
import { MatIconButton } from "@angular/material/button";
import { Organization } from "../../../common/models/organization";
import { User } from "../../../common/models/user";
import { UtilsProvider } from "../../../common/providers/utils-provider.service";
import { catchError, map, tap } from "rxjs/operators";
import { error } from "@angular/compiler-cli/src/transformers/util";

@Component({
  selector: 'team-view-page',
  providers: [],
  styleUrls: ['./view.scss'],
  standalone: true,
  imports: [
    HeaderComponent,
    Error401Component,
    MainActionComponent,
    ModuleContentComponent,
    Error404Component,
    PageContentComponent,
    MatPaginator,
    MatIconModule,
    MatTableModule,
    MatMenu,
    MatSortModule,
    NgIf,
    MatMenuTrigger,
    MatIconButton,
    TranslateModule,
  ],
  templateUrl: './view.html'
})
export class TeamViewPageComponent implements AfterViewInit, OnDestroy {

    public pageTitle = 'Team View';
    public isLoading = true;
    public is401 = false;
    public is404 = false;
    public isEditable = false;

    public organization: Organization = {} as Organization;

    public availableCoaches: User[] = [];
    public availableAthletes: User[] = [];

    public dataSource: SimpleMatDataSource = new SimpleMatDataSource([]);
    public columns: string[] = ['avatar', 'athlete', 'coach', 'actions'];

    @ViewChild(MatPaginator, {static: false}) public paginator: MatPaginator | undefined;
    @ViewChild(MatSort, {static: false}) public sorter: MatSort | undefined;

    public viewedTeam: TeamRow = {} as TeamRow;
    public viewedTeamName = '';
    public membersNumber = 0;
    public teamDescription = '';
    private _viewedTeamId = 0;
    private subscriptions: Subscription[] = [];

    constructor(
        public conf: ConfigProvider,
        public breadcrumbProvider: BreadcrumbProvider,
        public currentUser: CurrentUser,
        private _utils: UtilsProvider,
        private _title: Title,
        private _organizationNavigation: OrganizationsNavigationProvider,
        private _router: Router,
        private _currentUserRoles: CurrentUserRolesProvider,
        private _route: ActivatedRoute,
        private _translater: TranslateService,
        private _dialog: MatDialog,
        private _athletesProvider: AthletesProvider,
        private _teamsProvider: TeamsProvider,
        private _coachesProvider: CoachesProvider
    ) {
    }

  public ngAfterViewInit() {
    this.subscriptions.push(
      this._route.params.pipe(
        switchMap(params => {
          this._viewedTeamId = parseInt(params['teamId'], 10);
          return this._organizationNavigation.getOrganisationFromRoute();
        }),
        switchMap(organization => {
          if (!organization) {
            return throwError(() => new Error('Organization not found'));
          }
          this.organization = organization;
          this.initPage();
          return of();
        }),
        catchError(error => {
          console.error('An error occurred:', error);
          return of(null);
        })
      ).subscribe({
        next: () => {
        },
        error: error => console.error('Error during initialization:', error)
      })
    );
  }

    public ngOnDestroy(): void {
        this.subscriptions.forEach(subscription => subscription.unsubscribe());
    }

    // PUBLIC METHODS

    public onDisplayAthleteClicked(toView: AthleteRow) {

        const url = '/' + this.organization!.code + '/athletes/' + toView.athlete.userId;
        this._router.navigateByUrl(url);
    }

    public onRemoveAthleteFromTeamClicked(toRemove: AthleteRow) {
        // TODO: todo...
    }

    public onModifyTeamClicked(toModify: TeamRow): void {

        const dialogRef: any = this._dialog.open(TeamsFormComponent, {
            data: {
                organization: this.organization,
                availableCoaches: this.availableCoaches,
                availableAthletes: this.availableAthletes,
                teamRow: toModify,
                formTitle: 'Team_edition',
                confirmButtonTitle: this._translater.instant('Save')
            }
        });

        // màj de la liste des athlètes
      this.subscriptions.push(
        dialogRef.afterClosed().pipe(
          switchMap((row: TeamRow) => {
            if (row) {
              this.isLoading = true;  // Set loading to true immediately after dialog closes with a result
              return this.loadData();
            }
            return of(null); // If no row, just complete without doing anything
          })
        ).subscribe({
          next: () => {
            this.initNav();
            this.initSorters();
            this.isLoading = false;
          },
          error: (error: any) => {
            this.onError(error);
            this.isLoading = false;
          }
        })
      );
    }

    public onSortChange(event: any): void {
        this.dataSource.sortBy(event.active, event.direction);
    }

    // PRIVATE METHODS

    private loadOrganization(): Observable<any> {
        return this._organizationNavigation.getOrganisationFromRoute().pipe(
            map((organization: Organization | null) => {
                if (organization) {
                    this.organization = organization;
                    return organization;
                } else {
                    throw { error: 'Organization not found', status: 404 };
                }
            })
        );
    }

    private initPage(): void {
        this.loadData().subscribe({
            next: () => {
                this.initNav();
                this.initSorters();
                this.isLoading = false;
            },
            error: (error) => this.onError(error)
        })
    }

    private initNav(): void {
        if (!this.viewedTeam.rights?.view) {
            this.is401 = true;
            return;
        }
        this.isEditable = this.viewedTeam.rights?.edit || false;
        this.dataSource.paginator = this.paginator!;

        this.createPageTitle();
        this.createBreadcrumb();
    }

    private loadData(): Observable<any> {
      if (!this.organization?.id) {
        console.error('Organization not found');
        this.onError({ error: 'Organization not found', status: 404 });
        return of();
      }
      return forkJoin([
        this._teamsProvider.getTeamRow(this.organization.id, this._viewedTeamId),
        this._coachesProvider.getCoaches(this.organization.id).pipe(catchError(() => of([] as User[]))),
          this._athletesProvider.getAthletes(this.organization.id).pipe(catchError(() => of([] as User[]))),
      ]).pipe(
        map(([viewedTeam, availableCoaches, availableAthletes]) => {
            this.viewedTeam = viewedTeam;
            this.availableCoaches = availableCoaches;
            this.availableAthletes = availableAthletes;
            this.viewedTeamName = this.viewedTeam.team.name;
            this.membersNumber = this.viewedTeam.athletes.length;
            this.teamDescription = this.viewedTeam.team.description;

            const athletes: AthleteRow[] = this.viewedTeam.athletes.map((athlete) => {
              return this._athletesProvider.createAthleteRow(this.organization.id!, athlete);
            });

            athletes.sort((a, b) => this._utils.sortRow(
                this._utils.normalizeString(`${a.user.firstName} ${a.user.lastName}`),
                this._utils.normalizeString(`${b.user.firstName} ${b.user.lastName}`)
            ));
            this.dataSource.replaceAllWith(athletes);
        })
      );
    }

    private initSorters(): void {
        this.dataSource.addSortDefinition(
            'athlete',
            (a: AthleteRow, b: AthleteRow) => this._utils.sortRow((`${a.user.firstName} ${a.user.lastName}`).toLowerCase(), (`${b.user.firstName} ${b.user.lastName}`).toLowerCase()),
            (a: AthleteRow, b: AthleteRow) => this._utils.sortRow((`${b.user.firstName} ${b.user.lastName}`).toLowerCase(), (`${a.user.firstName} ${a.user.lastName}`).toLowerCase())
        );
        this.dataSource.addSortDefinition(
            'coach',
            (a: AthleteRow, b: AthleteRow) => this._utils.sortRow(a.athlete.externalCoachName ? a.athlete.externalCoachName.toLowerCase() : '', b.athlete.externalCoachName ? b.athlete.externalCoachName.toLowerCase() : ''),
            (a: AthleteRow, b: AthleteRow) => this._utils.sortRow(b.athlete.externalCoachName ? b.athlete.externalCoachName.toLowerCase() : '', a.athlete.externalCoachName ? a.athlete.externalCoachName.toLowerCase() : '')
        );
    }

    private createPageTitle(){
        this.pageTitle = this.viewedTeam.team.name;
        this._title.setTitle(this.pageTitle + ' | ' + this.conf.platformName);
    }

    private createBreadcrumb() {

        this.breadcrumbProvider.setDefaultCrumbs([{
            shortTitle: 'Teams',
            title: 'Teams',
            url: this.organization!.code + '/teams'
        }, {
            shortTitle: this.viewedTeam.team.name,
            title: this.viewedTeam.team.name,
            url: this._router.url
        }]);
    }

    private onError(error: any): void {
        this.isLoading = false;

        if (error.status === 404) {
            this.is404 = true;
        } else if (error.status === 401) {
            this.is401 = true;
        }
    }
}
